// Story 10.1 — TanStack Router code-based. Auth guard en `__root` redirige a
// `/login` cuando `GET /api/v1/auth/me` retorna 401.

import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { GlobalCommandPalette } from '@/components/GlobalCommandPalette';
import { AppShell } from '@/components/layout/AppShell';
import type { BotRowSummary } from '@/hooks/queries/use-bots';
import { type AuthUser, fetchMe } from '@/hooks/queries/use-me';
import { ApiError, analyticsApi, api, botsApi, conversationsApi, filesApi } from '@/lib/api-client';
import { parseOrganizationFilter } from '@/lib/bots-filter';
import { parseConversationFilters } from '@/lib/conversation-filters';
import type { Capability } from '@/lib/permissions';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import { assertCapabilityIn } from '@/lib/route-guards';
import { AccountPage } from '@/pages/AccountPage';
import { AnalyticsPage } from '@/pages/AnalyticsPage';
import { AuditPage } from '@/pages/AuditPage';
import { BotPage } from '@/pages/BotPage';
import { BotSettingsPage } from '@/pages/BotSettingsPage';
import { ConversationsPage } from '@/pages/ConversationsPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DocsPage } from '@/pages/DocsPage';
import { EditorPage } from '@/pages/EditorPage';
import { InvitePage } from '@/pages/InvitePage';
import { LoginPage } from '@/pages/LoginPage';
import { OrganizationPage } from '@/pages/OrganizationPage';
import { OrganizationsPage } from '@/pages/OrganizationsPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { UsersPage } from '@/pages/UsersPage';

/**
 * Story 10.5 — guard de capability. El componente destino lee
 * `?error=forbidden&capability=...` desde search y dispara un toast.
 */
/**
 * Story 16.2 — guard de una página de bot: decide con las capabilities que el
 * servidor devuelve con ese bot (rol del usuario en su organización). Sin
 * acceso, la API responde 404 y también se redirige.
 */
async function guardBotCapability(botId: number, capability: Capability): Promise<void> {
  let capabilities: Capability[] = [];
  try {
    const bot = await queryClient.ensureQueryData({
      queryKey: queryKeys.bot(botId),
      queryFn: () => api.get<BotRowSummary>(`/api/v1/bots/${botId}`),
    });
    capabilities = bot.capabilities ?? [];
  } catch (err) {
    if (!(err instanceof ApiError) || (err.status !== 404 && err.status !== 403)) throw err;
  }
  const fail = assertCapabilityIn(capabilities, capability);
  if (!fail) return;
  // biome-ignore lint/suspicious/noExplicitAny: TanStack search typing es restrictivo cross-route; el `/` ya valida con search schema relajado.
  throw redirect({ to: fail.to, search: fail.search as any });
}

/** Story 16.6 — guard de una capability de plataforma (p. ej. `manage_users`). */
async function guardCapability(capability: Capability): Promise<void> {
  const me = await ensureAuthenticated();
  const fail = assertCapabilityIn(me?.capabilities ?? [], capability);
  if (!fail) return;
  // biome-ignore lint/suspicious/noExplicitAny: TanStack search typing es restrictivo cross-route.
  throw redirect({ to: fail.to, search: fail.search as any });
}

async function ensureAuthenticated(): Promise<AuthUser | null> {
  // Misma query key y fetcher que `useMe`: el guard y los componentes ven la
  // misma forma (con organizaciones y capabilities).
  return queryClient.ensureQueryData({
    queryKey: queryKeys.me(),
    queryFn: fetchMe,
    staleTime: 60_000,
  });
}

const rootRoute = createRootRoute({
  component: Outlet,
});

function AuthedShell() {
  return (
    <>
      <AppShell />
      {/* Story 11.5 — paleta de comandos global (Cmd+K). Solo en las rutas con
          sesión: lista los bots, y en `/login`, `/invite` o `/reset-password`
          ese 401 disparaba el redirect global de "sesión expirada". */}
      <GlobalCommandPalette />
    </>
  );
}

// `/login` — sin AppShell, accesible siempre.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

// Story 16.5 — `/invite` — sin AppShell: quien recibe el link puede no tener
// cuenta todavía. El token viaja en el fragmento (`/invite#<token>`).
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite',
  component: InvitePage,
});

// Story 16.6 — `/reset-password` — sin AppShell: el link lo abre alguien sin
// sesión. El token viaja en el fragmento (`/reset-password#<token>`).
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

// Authenticated parent: AppShell + auth guard.
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authed',
  component: AuthedShell,
  beforeLoad: async ({ location }) => {
    const me = await ensureAuthenticated();
    if (!me) {
      throw redirect({
        to: '/login',
        search: { redirect: location.pathname },
      });
    }
    return { user: me };
  },
});

interface DashboardSearch {
  error?: 'forbidden';
  capability?: Capability;
  // P-317: preservar `q` y `sort` del DashboardPage (Story 10.2). Si no
  // los declarábamos, el cleanup `navigate({to: '/', search: prev})` tras
  // mostrar el toast forbidden los stripeaba del URL — el filtro/orden
  // del user se perdía cada vez que llegaba un redirect 403.
  q?: string;
  sort?: 'name' | 'activity' | 'status';
  // Hint del TopBar para abrir el wizard de creacion de bot al cargar.
  new?: number | string;
  // Story 16.5 — filtro por organización.
  org?: number;
}

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: DashboardPage,
  // Story 10.5 — search con `?error=forbidden&capability=...` cuando un guard
  // redirige acá. El componente lee y dispara toast.
  validateSearch: (search): DashboardSearch => {
    const raw = search as Record<string, unknown>;
    const error = raw.error === 'forbidden' ? 'forbidden' : undefined;
    const capability =
      typeof raw.capability === 'string' ? (raw.capability as Capability) : undefined;
    const q = typeof raw.q === 'string' && raw.q.length > 0 ? raw.q : undefined;
    const rawSort = typeof raw.sort === 'string' ? raw.sort : undefined;
    const sort: DashboardSearch['sort'] =
      rawSort === 'name' || rawSort === 'activity' || rawSort === 'status' ? rawSort : undefined;
    return { error, capability, q, sort, org: parseOrganizationFilter(raw) };
  },
  // IG-26 (Task 6.1): pre-hidratamos la lista de bots vía loader para que
  // la query `['bots']` esté lista antes del render — evita un flash de
  // skeletons en la primera navegación a `/`.
  loader: () =>
    queryClient.ensureQueryData({
      queryKey: ['bots'] as const,
      queryFn: () => botsApi.list(),
    }),
});

const botOverviewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/bots/$botId',
  component: BotPage,
  // P-221: validar `botId` numérico antes de pasar al loader. Sin esto,
  // `/bots/abc` arranca un fetch a `/api/v1/bots/abc` que retorna 404 y
  // el cache key `['bots', NaN]` queda contaminado para futuras visitas.
  parseParams: (params) => {
    if (!/^\d+$/.test(params.botId)) {
      throw new Error(`Invalid botId: ${params.botId}`);
    }
    return { botId: params.botId };
  },
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: ['bots', Number(params.botId)] as const,
      queryFn: () => api.get(`/api/v1/bots/${params.botId}`),
    }),
});

const botEditorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/bots/$botId/editor',
  component: EditorPage,
  parseParams: (params) => {
    if (!/^\d+$/.test(params.botId)) {
      throw new Error(`Invalid botId: ${params.botId}`);
    }
    return { botId: params.botId };
  },
  beforeLoad: ({ params }) => guardBotCapability(Number(params.botId), 'edit_bot'),
  loader: ({ params }) => {
    const botId = Number(params.botId);
    return queryClient.ensureQueryData({
      queryKey: queryKeys.files(botId),
      queryFn: () => filesApi.tree(botId),
    });
  },
});

interface ConversationsSearch {
  c?: string;
  since?: string;
  until?: string;
  flow?: string;
  status?: 'active' | 'expired' | 'completed';
  q?: string;
}

const botConversationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/bots/$botId/conversations',
  component: ConversationsPage,
  parseParams: (params) => {
    if (!/^\d+$/.test(params.botId)) {
      throw new Error(`Invalid botId: ${params.botId}`);
    }
    return { botId: params.botId };
  },
  validateSearch: (search): ConversationsSearch => {
    const filters = parseConversationFilters(search as Record<string, unknown>);
    const c =
      typeof (search as Record<string, unknown>).c === 'string'
        ? ((search as Record<string, unknown>).c as string)
        : undefined;
    return { ...filters, c };
  },
  loaderDeps: ({ search }) => ({
    since: search.since,
    until: search.until,
    flow: search.flow,
    status: search.status,
    q: search.q,
  }),
  loader: ({ params, deps }) => {
    const botId = Number(params.botId);
    const filters = parseConversationFilters(deps as Record<string, unknown>);
    return queryClient.ensureInfiniteQueryData({
      queryKey: queryKeys.conversationsList(botId, filters),
      queryFn: () => conversationsApi.list(botId, { ...filters, limit: 20, offset: 0 }),
      initialPageParam: 0,
    });
  },
});

interface AnalyticsSearch {
  since?: string;
  until?: string;
}

const botAnalyticsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/bots/$botId/analytics',
  component: AnalyticsPage,
  parseParams: (params) => {
    if (!/^\d+$/.test(params.botId)) {
      throw new Error(`Invalid botId: ${params.botId}`);
    }
    return { botId: params.botId };
  },
  validateSearch: (search): AnalyticsSearch => {
    const raw = search as Record<string, unknown>;
    const since = typeof raw.since === 'string' ? raw.since : undefined;
    const until = typeof raw.until === 'string' ? raw.until : undefined;
    return { since, until };
  },
  loaderDeps: ({ search }) => ({ since: search.since, until: search.until }),
  loader: ({ params, deps }) => {
    const botId = Number(params.botId);
    return queryClient.ensureQueryData({
      queryKey: queryKeys.analytics(botId, deps.since, deps.until),
      queryFn: () => analyticsApi.get(botId, { since: deps.since, until: deps.until }),
    });
  },
});

const botSettingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/bots/$botId/settings',
  component: BotSettingsPage,
  parseParams: (params) => {
    if (!/^\d+$/.test(params.botId)) {
      throw new Error(`Invalid botId: ${params.botId}`);
    }
    return { botId: params.botId };
  },
  beforeLoad: ({ params }) => guardBotCapability(Number(params.botId), 'view_settings'),
});

// ─── Organizaciones (Story 16.5) ────────────────────────────────────────
const organizationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/organizations',
  component: OrganizationsPage,
});
const organizationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/organizations/$organizationId',
  component: OrganizationPage,
  parseParams: (params) => {
    if (!/^\d+$/.test(params.organizationId)) {
      throw new Error(`Invalid organizationId: ${params.organizationId}`);
    }
    return { organizationId: params.organizationId };
  },
});

// ─── Cuenta y usuarios (Story 16.6) ─────────────────────────────────────
const accountRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/account',
  component: AccountPage,
});
// Story 16.7 — el admin y los owners (los que gestionan miembros en alguna).
const auditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/audit',
  component: AuditPage,
  beforeLoad: () => guardCapability('manage_members'),
});
const usersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/users',
  component: UsersPage,
  beforeLoad: () => guardCapability('manage_users'),
});

// ─── Docs (publica + indexada por slug) ─────────────────────────────────
const docsIndexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/docs',
  component: DocsPage,
});
const docsTopicRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/docs/$slug',
  component: DocsPage,
  parseParams: (params) => {
    if (!/^[a-z][a-z0-9-]*$/.test(params.slug)) {
      throw new Error(`Invalid docs slug: ${params.slug}`);
    }
    return { slug: params.slug };
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  inviteRoute,
  resetPasswordRoute,
  authedRoute.addChildren([
    dashboardRoute,
    botOverviewRoute,
    botEditorRoute,
    botConversationsRoute,
    botAnalyticsRoute,
    botSettingsRoute,
    organizationsRoute,
    organizationRoute,
    accountRoute,
    usersRoute,
    auditRoute,
    docsIndexRoute,
    docsTopicRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { queryClient },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
